import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import {
  AuthService,
  IEmailVerificationChallenge,
  ISecondFactorChallenge,
  ITotpEnrolment,
  SecondFactorChannel,
  SecondFactorType,
} from '@services/auth.service';
import { toDataURL } from 'qrcode';
import { Observable, Subscription, timer } from 'rxjs';
import { RESEND_PAUSE_MS, VerificationEnd } from '../email-verification-step/email-verification-step.component';
import { goneReason, normaliseCode, parseErrorBody, retryTime } from '../login-responses';

/** The calls behind setting a factor up from My Account, where nothing signs in. */
export interface SecondFactorSetupApi {
  addPhone(challenge: string, phoneNumber: string): Observable<unknown>;
  addApp(challenge: string): Observable<ITotpEnrolment>;
  sendCode(challenge: string, channel: SecondFactorChannel): Observable<unknown>;
  confirm(challenge: string, type: SecondFactorType, code: string): Observable<unknown>;
}

interface Notice {
  key: string;
  params?: Record<string, unknown>;
  tone: 'error' | 'info';
}

/**
 * The second step a correct password leads to (openspec add-second-factor-sign-in, design section 1): a code
 * from the user's phone or authenticator app, or setting one up when they hold neither. The challenge stays
 * in memory. The secret is shown once, during enrolment, and never again.
 */
@Component({
  selector: 'app-second-factor-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  templateUrl: './second-factor-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-4' },
})
export class SecondFactorStepComponent implements OnInit, OnDestroy {
  @Input() challenge!: ISecondFactorChallenge;
  /** Set on My Account: these calls instead of sign-in's, starting straight on <see cref="addingType"/>. */
  @Input() adding: SecondFactorSetupApi | null = null;
  @Input() addingType: SecondFactorType = 'totp';
  @Output() signedIn = new EventEmitter<void>();
  @Output() emailFallback = new EventEmitter<IEmailVerificationChallenge>();
  @Output() ended = new EventEmitter<VerificationEnd>();

  readonly codeForm = new FormGroup({
    code: new FormControl('', { nonNullable: true }),
  });
  readonly numberForm = new FormGroup({
    phoneNumber: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  step: 'choose' | 'number' | 'scan' | 'code' = 'code';
  /** The factor the code step is asking for. */
  factor: SecondFactorType = 'totp';
  channel: SecondFactorChannel = 'sms';
  /** Where a phone code went, masked or as typed. */
  phoneHint: string | null = null;
  qrDataUrl: string | null = null;
  manualKey: string | null = null;
  notice: Notice | null = null;
  busy = false;
  resendPaused = false;
  sendLimitReached = false;
  /** No code could be sent, so there is nothing to type. */
  cannotSend = false;

  private resendPause?: Subscription;

  constructor(
    private _as: AuthService,
    private _cdr: ChangeDetectorRef,
    @Inject(RESEND_PAUSE_MS) private _resendPauseMs: number,
    @Inject(LOCALE_ID) private _locale: string
  ) {}

  get token(): string {
    return this.challenge.challenge as string;
  }

  get codeIsComplete(): boolean {
    return /^\d{6}$/.test(normaliseCode(this.codeForm.controls.code.value));
  }

  /** The other factor the user holds, to offer beside this one. */
  get otherFactor(): SecondFactorType | null {
    return this.challenge.factors.find(f => f.type !== this.factor)?.type ?? null;
  }

  ngOnInit() {
    if (this.challenge.enrolment) {
      this.step = 'choose';
      this.numberForm.controls.phoneNumber.setValue(this.challenge.suggestedPhoneNumber ?? '');
      if (this.adding) {
        this.addingType === 'sms' ? this.choosePhone() : this.chooseApp();
      }
      return;
    }

    this.step = 'code';
    this.phoneHint = this.challenge.factors.find(f => f.type === 'sms')?.hint ?? null;
    this.factor = this.challenge.defaultFactor ?? this.challenge.factors[0]?.type ?? 'totp';
    if (this.factor === 'sms') {
      this.sendCode('sms');
    }
  }

  ngOnDestroy() {
    this.resendPause?.unsubscribe();
  }

  choosePhone() {
    this.notice = null;
    this.step = 'number';
  }

  chooseApp() {
    this.start();
    (this.adding ? this.adding.addApp(this.token) : this._as.enrolSecondFactorTotp(this.token)).subscribe({
      next: enrolment => {
        this.factor = 'totp';
        this.manualKey = enrolment.manualKey;
        this.codeForm.reset();
        // The QR code is drawn here, in the browser, so the secret goes to no image service.
        toDataURL(enrolment.otpauthUri, { margin: 1, width: 192 }).then(url => {
          this.qrDataUrl = url;
          this.busy = false;
          this.step = 'scan';
          this._cdr.markForCheck();
        });
      },
      error: (error: HttpErrorResponse) => this.onSendError(error),
    });
  }

  chooseDifferent() {
    this.notice = null;
    this.cannotSend = false;
    this.resendPause?.unsubscribe();
    this.resendPaused = false;
    this.step = 'choose';
  }

  useDifferentNumber() {
    this.notice = null;
    this.resendPause?.unsubscribe();
    this.resendPaused = false;
    this.step = 'number';
  }

  submitNumber() {
    if (this.busy || this.numberForm.invalid) {
      return;
    }
    const number = this.numberForm.controls.phoneNumber.value.trim();
    const request = this.adding ? this.adding.addPhone(this.token, number) : this._as.enrolSecondFactorPhone(this.token, number);
    this.send(request, () => {
      this.factor = 'sms';
      this.channel = 'sms';
      this.phoneHint = lastDigits(number);
      this.step = 'code';
      this.codeForm.reset();
    });
  }

  /** Text, call or resend. Sends are counted together, and only the newest code works. */
  sendCode(channel: SecondFactorChannel) {
    const resending = this.resendPaused || this.channel === channel;
    const request = this.adding ? this.adding.sendCode(this.token, channel) : this._as.sendSecondFactorCode(this.token, channel);
    this.send(request, () => {
      this.channel = channel;
      this.factor = 'sms';
      this.step = 'code';
      if (resending && this.codeForm.controls.code.value) {
        this.codeForm.reset();
      }
    });
  }

  switchTo(factor: SecondFactorType) {
    this.notice = null;
    this.cannotSend = false;
    this.codeForm.reset();
    if (factor === 'sms') {
      this.sendCode('sms');
    } else {
      this.factor = 'totp';
      this.step = 'code';
    }
  }

  retry() {
    this.cannotSend = false;
    this.sendCode(this.channel);
  }

  verify() {
    if (this.busy || !this.codeIsComplete) {
      return;
    }
    const code = normaliseCode(this.codeForm.controls.code.value);
    this.start();
    if (this.adding) {
      this.adding.confirm(this.token, this.factor, code).subscribe({
        next: () => this.signedIn.emit(),
        error: (error: HttpErrorResponse) => this.onVerifyError(error),
      });
      return;
    }
    const request = this.challenge.enrolment
      ? this._as.confirmSecondFactor(this.token, this.factor, code)
      : this._as.verifySecondFactorCode(this.token, this.factor, code);
    request.subscribe({
      next: signedIn => (signedIn ? this.signedIn.emit() : this.ended.emit('rejected')),
      error: (error: HttpErrorResponse) => this.onVerifyError(error),
    });
  }

  useEmail() {
    this.start();
    this._as.secondFactorEmailFallback(this.token).subscribe({
      next: emailStep => this.emailFallback.emit(emailStep),
      error: (error: HttpErrorResponse) => this.onSendError(error),
    });
  }

  back() {
    this.ended.emit('back');
  }

  private start() {
    this.busy = true;
    this.notice = null;
    this._cdr.markForCheck();
  }

  private send(request: Observable<unknown>, onSent: () => void) {
    this.start();
    request.subscribe({
      next: () => {
        this.busy = false;
        this.cannotSend = false;
        onSent();
        this.pauseResend();
        this._cdr.markForCheck();
      },
      error: (error: HttpErrorResponse) => this.onSendError(error),
    });
  }

  private onSendError(error: HttpErrorResponse) {
    this.busy = false;
    this._cdr.markForCheck();
    if (error.status === 410) {
      this.ended.emit(goneReason(error));
      return;
    }

    const body = parseErrorBody(error.error);
    if (error.status === 429) {
      this.sendLimitReached = true;
      this.notice =
        body?.limit === 'phone'
          ? { key: 'Login.CodeLimitResend', tone: 'error', params: { time: retryTime(body.retryAfter, this._locale) } }
          : { key: 'Login.CodeLimitChallenge', tone: 'error' };
      return;
    }
    if (body?.reason === 'number') {
      this.notice = { key: 'Login.MfaNumberRefused', tone: 'error' };
      return;
    }
    if (body?.reason === 'unavailable') {
      if (this.step === 'number') {
        this.notice = { key: 'Login.Failed', tone: 'error' };
      } else {
        this.cannotSend = true;
      }
      return;
    }
    this.notice = { key: 'Login.Failed', tone: 'error' };
  }

  private onVerifyError(error: HttpErrorResponse) {
    this.busy = false;
    this._cdr.markForCheck();
    if (error.status === 410) {
      this.ended.emit(goneReason(error));
      return;
    }

    const body = parseErrorBody(error.error);
    if (error.status === 503) {
      this.notice = { key: 'Login.MfaCannotCheck', tone: 'error' };
    } else if (error.status === 400 && typeof body?.attemptsLeft === 'number') {
      if (body.attemptsLeft === 0) {
        this.ended.emit('too-many-codes');
        return;
      }
      this.notice = {
        key: body.attemptsLeft === 1 ? 'Login.CodeWrongLast' : 'Login.CodeWrong',
        params: { attempts: body.attemptsLeft },
        tone: 'error',
      };
    } else if (body?.passwordResetRequired) {
      this.ended.emit('reset-required');
    } else if (body?.success === false) {
      this.ended.emit('rejected');
    } else {
      this.notice = { key: 'Login.Failed', tone: 'error' };
    }
  }

  private pauseResend() {
    this.resendPause?.unsubscribe();
    this.resendPaused = true;
    this.resendPause = timer(this._resendPauseMs).subscribe(() => {
      this.resendPaused = false;
      this._cdr.markForCheck();
    });
  }
}

/** The same mask the server gives a number on file. */
function lastDigits(number: string): string {
  return `•••• ${number.replace(/\D/g, '').slice(-4)}`;
}
